import ClassNames from '@streamloots/classnames';
import { PageFeedPermission } from '@streamloots/streamloots-types';
import { PageFeed, PageFeedPaymentStatus } from 'model/indexTS';
import { useMemo } from 'react';
import { AuthDialog } from 'scenes/auth';
import { SubscriptionCheckoutDialog } from 'scenes/subscription-checkout';
import { useAuthSelectors } from 'services/auth';
import Button from 'ui-library/button';
import { DialogOpener } from 'ui-library/dialog';
import trackEventManager from 'utils/event-manager';
import { TranslateInterface } from 'utils/translation';
import { CheckoutUnlock } from '../checkout-unlock';
import theme from './feed-list.scss';
import { useFeedList } from './useFeedList';

const classNames = ClassNames(theme);

export interface ObfuscatedUnlockModalOpenerProps extends TranslateInterface {
  feed: PageFeed;
}

export const ObfuscatedUnlockModalOpener = ({ feed, t }: ObfuscatedUnlockModalOpenerProps): JSX.Element | null => {
  const { page } = useFeedList();
  const subscribeToUnlock = feed.restrictedTo.includes(PageFeedPermission.ONLY_SUBSCRIBERS);
  const { isUserAuthenticated } = useAuthSelectors();

  const handleSubscribe = (onShow: () => void) => {
    if (subscribeToUnlock) {
      trackEventManager.subscriptionPurchasedClick({
        location: 'feed exclusive content',
      });
    }
    trackEventManager.feedClicked({
      button: subscribeToUnlock ? 'subscribe to unlock' : 'pay to unlock',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });

    onShow();
  };

  const buttonStatus = useMemo(() => {
    if (subscribeToUnlock) {
      return {
        title: 'pageFeed:obfuscated.subscribeButton',
        disabled: false,
      };
    }

    if (!feed.summary.paymentStatus) {
      return {
        title: 'pageFeed:obfuscated.payButton',
        disabled: false,
      };
    }

    if (feed.summary.paymentStatus === PageFeedPaymentStatus.InProgress) {
      return {
        title: 'pageFeed:unlockCheckout.success',
        disabled: true,
        classname: 'feed-detail__offuscated__button--payment-in-process',
      };
    }

    return {
      title: 'pageFeed:unlockCheckout.error',
      disabled: true,
      classname: 'feed-detail__offuscated__button--payment-error',
    };
  }, [feed.summary.paymentStatus, subscribeToUnlock]);

  if (page.isOwner || (subscribeToUnlock && !page.modules?.subscriptions.enabled)) {
    return null;
  }

  return (
    <DialogOpener
      fullOnSmall
      dialogClassName={classNames('modal')}
      type={!isUserAuthenticated ? 'auth' : 'normal'}
      renderDialogBody={({ onClose }) => {
        if (!isUserAuthenticated) {
          return <AuthDialog onClose={onClose} componentKey={'my_feed'} location={'feed content'} />;
        }

        if (subscribeToUnlock) {
          return <SubscriptionCheckoutDialog onClose={onClose} />;
        }

        return <CheckoutUnlock onClose={onClose} feed={feed} page={page} />;
      }}
      renderOpener={({ onShow }) => (
        <Button
          color="info"
          variant="filled"
          className={classNames({
            [buttonStatus.classname ?? '']: Boolean(buttonStatus.classname),
          })}
          label={t(buttonStatus.title)}
          onClick={() => handleSubscribe(onShow)}
          disabled={buttonStatus.disabled}
        />
      )}
    />
  );
};
